import { supabase } from "@/lib/supabase/client";
import type { Database } from "@/lib/supabase/types";
import type { OrganizationRepository } from "@/data/repositories/organizationRepository";
import type { OrganizationV2 } from "@/types/domain/organization";
import type { CreateOrganizationInput, UpdateOrganizationInput } from "@/data/repositories/organizationRepository";

type OrganizationRow = Database["public"]["Tables"]["organizations"]["Row"];

function organizationWriteError(error: { code?: string; message?: string }): Error {
  if (error.code === "23505") return new Error("That workspace identifier is already in use. Choose another one.");
  if (error.code === "42501") return new Error("Your session cannot create organizations. Sign out, sign in again, and retry.");
  if (error.code === "23503") return new Error("Your user profile is incomplete. Sign out, sign in again, and retry.");
  if (error.message?.toLowerCase().includes("jwt")) return new Error("Your session expired. Sign in again and retry.");
  return new Error("Unable to save organization. Please retry; if it continues, contact support.");
}

function rowToOrganization(row: OrganizationRow): OrganizationV2 {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    organizationType: row.organization_type as OrganizationV2["organizationType"],
    logoUrl: row.logo_url ?? undefined,
    phone: row.phone ?? undefined,
    email: row.email ?? undefined,
    websiteUrl: row.website_url ?? undefined,
    timezone: row.timezone,
    currency: row.currency,
    countryCode: row.country_code,
    subscriptionPlan: row.subscription_plan as OrganizationV2["subscriptionPlan"],
    subscriptionStatus: row.subscription_status as OrganizationV2["subscriptionStatus"],
    trialEndsAt: row.trial_ends_at ?? undefined,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * Real implementation, wired against the `organizations` /
 * `organization_members` tables from
 * supabase/migrations/0001_core_platform.sql. Not selected by default
 * anywhere (see src/data/repositories/index.ts) until that migration has
 * actually been applied to a live project — dummy mode is what runs today.
 *
 * Deliberately two queries rather than one nested `select` join: keeps the
 * Database placeholder types simple (no hand-written Relationships
 * metadata) until `supabase gen types` produces the real thing.
 */
export class SupabaseOrganizationRepository implements OrganizationRepository {
  async listForUser(userId: string): Promise<OrganizationV2[]> {
    const { data: memberships, error: membershipError } = await supabase
      .from("organization_members")
      .select("organization_id")
      .eq("user_id", userId)
      .eq("status", "active");
    if (membershipError) throw membershipError;

    const organizationIds = (memberships ?? []).map((m) => m.organization_id);
    if (organizationIds.length === 0) return [];

    const { data, error } = await supabase.from("organizations").select("*").in("id", organizationIds);
    if (error) throw error;
    return (data ?? []).map(rowToOrganization);
  }

  async getById(id: string): Promise<OrganizationV2 | null> {
    const { data, error } = await supabase.from("organizations").select("*").eq("id", id).maybeSingle();
    if (error) throw error;
    return data ? rowToOrganization(data) : null;
  }

  async create(input: CreateOrganizationInput): Promise<OrganizationV2> {
    const { data, error } = await supabase
      .from("organizations")
      .insert({
        name: input.name,
        slug: input.slug,
        organization_type: input.organizationType,
        timezone: input.timezone,
        currency: input.currency,
        country_code: input.countryCode,
        created_by: input.createdBy,
      })
      .select("*")
      .single();
    if (error) throw organizationWriteError(error);
    return rowToOrganization(data);
  }

  async update(id: string, patch: UpdateOrganizationInput): Promise<OrganizationV2> {
    const { data, error } = await supabase
      .from("organizations")
      .update({
        ...(patch.name !== undefined && { name: patch.name }),
        ...(patch.slug !== undefined && { slug: patch.slug }),
        ...(patch.organizationType !== undefined && { organization_type: patch.organizationType }),
        ...(patch.timezone !== undefined && { timezone: patch.timezone }),
        ...(patch.currency !== undefined && { currency: patch.currency }),
        ...(patch.countryCode !== undefined && { country_code: patch.countryCode }),
        ...(patch.phone !== undefined && { phone: patch.phone }),
        ...(patch.email !== undefined && { email: patch.email }),
        ...(patch.websiteUrl !== undefined && { website_url: patch.websiteUrl }),
      })
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw organizationWriteError(error);
    return rowToOrganization(data);
  }

  async delete(id: string): Promise<void> {
    const { data, error } = await supabase.from("organizations").delete().eq("id", id).select("id").maybeSingle();
    if (error) throw error;
    if (!data) throw new Error("The organization could not be deleted. Only its owner can perform this action.");
  }
}
